/**
 * idbMirrorBackend.ts — Backend dual: escribe en localStorage (síncrono) +
 * IndexedDB (durable, async fire-and-forget).
 *
 * POR QUÉ EXISTE:
 *  El motor transaccional (transactionalStore) es síncrono y debe seguir siéndolo
 *  para garantizar aislamiento single-turn. localStorage es síncrono pero NO
 *  durable en Android WebView: el sistema operativo puede vaciarlo al matar el
 *  proceso. IndexedDB SÍ sobrevive a kills de proceso porque Android lo trata como
 *  almacenamiento de origen, no como cache de sesión del WebView.
 *
 *  Estrategia dual-write:
 *   1. Toda escritura va a localStorage (lectura síncrona sin cambios).
 *   2. La misma escritura se espeja en IndexedDB (async, best-effort).
 *   3. Al arrancar, si localStorage está vacío/sparse pero IDB tiene datos →
 *      se restaura desde IDB ANTES de recoverStore() → tolerancia a kills de APK.
 *
 * GARANTÍAS:
 *  - No rompe atomicidad: el mirror IDB es posterior al commit en LS.
 *  - No bloquea el hilo JS: el espejado IDB es fire-and-forget.
 *  - En caso de fallo IDB (entorno sin IDB): graceful degradation a LS solo.
 *  - La restauración desde IDB es idempotente (solo escribe claves ausentes en LS).
 *
 * QUÉ EXPORTA:
 *  - idbMirrorBackend: StorageBackend (para getActiveBackend)
 *  - restoreFromIDBMirror(): Promise<number> (llamar al arrancar antes de recoverStore)
 *
 * QUÉ NO DEBE HACER:
 *  - No importar React, hooks ni componentes.
 *  - No contener lógica de negocio.
 *  - No hacer await dentro de transacciones (rompe aislamiento síncrono).
 */

import { logger } from "@/lib/logger";
import { localStorageBackend } from "./storageBackend";
import type { StorageBackend } from "./storageBackend";
import { persistenceMetrics } from "./persistenceMetrics";

// ── IndexedDB helpers (raw, sin namespace para máxima velocidad) ─────────────

const IDB_NAME = "altenot_mirror_v1";
const IDB_STORE = "kv";
let _db: IDBDatabase | null = null;
let _dbPromise: Promise<IDBDatabase> | null = null;

function openMirrorDB(): Promise<IDBDatabase> {
  if (_db) return Promise.resolve(_db);
  if (_dbPromise) return _dbPromise;

  _dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    try {
      const req = indexedDB.open(IDB_NAME, 1);
      req.onupgradeneeded = (e) => {
        const db = (e.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(IDB_STORE)) {
          db.createObjectStore(IDB_STORE);
        }
      };
      req.onsuccess = (e) => {
        _db = (e.target as IDBOpenDBRequest).result;
        resolve(_db);
      };
      req.onerror = () => {
        _dbPromise = null;
        reject(req.error);
      };
    } catch (err) {
      _dbPromise = null;
      reject(err);
    }
  });

  return _dbPromise;
}

/** Fire-and-forget: escribe el string final en IDB sin re-serializar. */
function idbPutAsync(key: string, serialized: string): void {
  openMirrorDB()
    .then((db) => {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.objectStore(IDB_STORE).put(serialized, key);
    })
    .catch(() => {
      /* IDB no disponible: ya está en LS, no es crítico */
    });
}

/** Fire-and-forget: elimina en IDB sin bloquear. */
function idbRemoveAsync(key: string): void {
  openMirrorDB()
    .then((db) => {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.objectStore(IDB_STORE).delete(key);
    })
    .catch(() => {
      /* no-op */
    });
}

/** Lee TODAS las entradas de IDB. Exportado para diagnóstico/corrupción. */
export async function idbGetAll(): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  try {
    const db = await openMirrorDB();
    return await new Promise<Map<string, string>>((resolve) => {
      const tx = db.transaction(IDB_STORE, "readonly");
      const store = tx.objectStore(IDB_STORE);
      const keysReq = store.getAllKeys();
      const valsReq = store.getAll();

      tx.oncomplete = () => {
        const keys = keysReq.result as string[];
        const vals = valsReq.result as string[];
        keys.forEach((k, i) => result.set(k, vals[i]));
        resolve(result);
      };
      tx.onerror = () => resolve(result);
    });
  } catch {
    return result;
  }
}

// ── Backend dual-write ────────────────────────────────────────────────────────

/**
 * StorageBackend que escribe en localStorage (síncrono) y espejaen IDB (async).
 * Lecturas vienen de localStorage (más rápido, ya en memoria del proceso).
 */
export const idbMirrorBackend: StorageBackend = {
  get<T>(key: string, defaultValue: T): T {
    return localStorageBackend.get<T>(key, defaultValue);
  },

  serialize<T>(value: T): string {
    return localStorageBackend.serialize(value);
  },

  set<T>(key: string, value: T): void {
    // Serializa UNA vez y reutiliza la cadena en LS + espejo IDB (sin volver a
    // JSON.stringify ni comprimir en el mirror: evita picos síncronos post-commit).
    const serialized = localStorageBackend.serialize(value);
    localStorageBackend.setSerialized<T>(key, serialized, value);
    idbPutAsync(key, serialized);
  },

  setSerialized<T>(key: string, serialized: string, original?: T): void {
    localStorageBackend.setSerialized<T>(key, serialized, original);
    idbPutAsync(key, serialized);
  },

  getRaw(key: string): string | null {
    return localStorageBackend.getRaw(key);
  },

  setRaw(key: string, value: string): void {
    localStorageBackend.setRaw(key, value);
    idbPutAsync(key, String(value));
  },

  remove(key: string): void {
    localStorageBackend.remove(key);
    idbRemoveAsync(key);
  },
};

// ── CLAVES CRÍTICAS de negocio que deben sobrevivir a un kill de APK ─────────

/**
 * Claves de negocio que, si están en IDB pero no en LS, deben restaurarse.
 * Excluye: claves de UI, WAL journal (se reaplica solo), demos flags.
 * Ajustar si se agregan módulos nuevos.
 */
const BUSINESS_KEYS_PREFIXES = [
  "caja_",
  "auditoria_",
  "inventory_",
  "personal_",
  "tienda_",
  "creditos_",
  "fidelizacion_",
  "payroll_",
  "higiene_",
  "businessConfig",
  "altenot_punto_caja_id",
  "__altenot_wal__",
];

function isBusinessKey(key: string): boolean {
  return BUSINESS_KEYS_PREFIXES.some((p) => key.startsWith(p));
}

/** Estado técnico interno: se recupera al arrancar, pero no pertenece a un respaldo portable. */
const BACKUP_EXCLUDED_KEYS = new Set(["__altenot_wal__"]);

/**
 * Devuelve una instantánea serializada de las claves operativas del negocio.
 * Se limita a esta capa para que servicios de dominio no enumeren localStorage.
 */
export async function getBusinessStorageSnapshot(): Promise<
  Record<string, string>
> {
  const snapshot: Record<string, string> = {};
  const mirrored = await idbGetAll();
  for (const [key, value] of mirrored) {
    if (
      isBusinessKey(key) &&
      !SKIP_RESTORE_KEYS.has(key) &&
      !BACKUP_EXCLUDED_KEYS.has(key)
    )
      snapshot[key] = value;
  }
  try {
    Object.keys(localStorage).forEach((key) => {
      const value = localStorage.getItem(key);
      if (
        isBusinessKey(key) &&
        !SKIP_RESTORE_KEYS.has(key) &&
        !BACKUP_EXCLUDED_KEYS.has(key) &&
        value !== null
      ) {
        snapshot[key] = value;
      }
    });
  } catch {
    /* IndexedDB ya aporta la instantánea disponible en entornos restringidos. */
  }
  return snapshot;
}

// ── Claves que NUNCA deben restaurarse desde IDB (banderas demo/UI efímeras) ──

const SKIP_RESTORE_KEYS = new Set([
  "altenot_caja_demo_fresh_applied",
  "altenot_caja_demo_cierre_seed",
  "altenot_caja_demo_may12_seed",
  "altenot_demo_jornada_calendar_day",
]);

/**
 * Restaura localStorage desde el espejo IDB cuando LS está vacío o le faltan
 * claves de negocio (típico tras kill de proceso en Android).
 *
 * Debe llamarse ANTES de `recoverStore()` en el bootstrap de la app.
 *
 * @returns número de claves restauradas (0 = LS ya tenía todos los datos).
 */
export async function restoreFromIDBMirror(): Promise<number> {
  const start = Date.now();
  let restored = 0;

  try {
    const idbEntries = await idbGetAll();
    if (idbEntries.size === 0) return 0;

    // Detectar si LS parece vacío: contar claves de negocio en LS
    const lsBusinessKeys = Object.keys(localStorage).filter(isBusinessKey);

    // Heurística: si IDB tiene significativamente más claves de negocio que LS
    // (o LS no tiene ninguna clave de negocio) → restaurar las que faltan.
    const idbBusinessKeys = [...idbEntries.keys()].filter(isBusinessKey);
    const lsMissing = idbBusinessKeys.filter(
      (k) => !SKIP_RESTORE_KEYS.has(k) && localStorage.getItem(k) === null,
    );

    if (lsMissing.length === 0) return 0;

    // Umbral conservador: restaurar si faltan ≥1 clave de negocio O si LS
    // tiene menos del 30% de las claves de negocio que IDB.
    const shouldRestore =
      lsBusinessKeys.length === 0 ||
      lsMissing.length >= Math.max(1, Math.floor(idbBusinessKeys.length * 0.3));

    if (!shouldRestore) return 0;

    for (const key of lsMissing) {
      const serialized = idbEntries.get(key);
      if (serialized === undefined) continue;

      // El espejo IDB guarda SIEMPRE el string ya serializado (JSON o crudo),
      // idéntico a como vive en localStorage. La restauración es un copy-through
      // literal: no hay que re-serializar ni distinguir raw vs JSON.
      try {
        localStorage.setItem(key, serialized);
        restored++;
      } catch {
        /* cuota: ya manejada por setStorage */
      }
    }

    if (restored > 0) {
      persistenceMetrics.recordRestore(lsMissing.slice(0, restored));
      logger.info(
        `[IDB Mirror] Restauradas ${restored} claves desde IndexedDB` +
          ` (LS tenía ${lsBusinessKeys.length}, IDB tenía ${idbBusinessKeys.length}).` +
          ` (${Date.now() - start}ms)`,
      );
    }
  } catch (err) {
    logger.warn("[IDB Mirror] Restauración falló, continuando con LS:", err);
  }

  return restored;
}
